/**
 * UtilizationForecastChart
 *
 * Capacity-vs-demand projection by month. Three series, projected
 * across 3 months back + 12 months forward:
 *
 *   Available       — engineer-hours the team can deliver each month, off
 *                     the Manpower calendar (r49, Sharif 10/2/26): for each
 *                     NA engineer who is not an intern,
 *                     (weekdays in the month - their PTO weekdays) x 8 hrs.
 *                     So weekends, PTO and interns are out - the same roster
 *                     and day rules as the Engineer Utilization chart
 *                     (utils/utilizationFromCalendar availableHours). It used
 *                     to be a flat headcount x 40 hrs/week x 4 weeks, which
 *                     counted PTO weeks as hours the team could sell.
 *
 *   Demand          — hours already committed to Won projects.
 *                     PLACEHOLDER (zero) until live quote-hours data
 *                     is integrated. Keeping the series wired so the
 *                     chart lights up automatically once data lands.
 *
 *   Forecast Demand — hours that would land if every Open quote wins.
 *                     PLACEHOLDER (zero) for the same reason.
 *
 * View modes:
 *   hours   — y-axis is engineer-hours per month
 *   percent — y-axis is % of Available (Available pegs at 100)
 */

import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { availableHours, utilizationRoster } from '../../utils/utilizationFromCalendar';

/** How many people the Available line counts: NA, not archived, not interns,
 *  in a calendar table that counts toward utilization (`layout`).
 *  The caption next to the chart quotes this so it matches the line. */
export const capacityHeadcount = (engineers, layout) => utilizationRoster(Array.isArray(engineers) ? engineers : [], layout).length;

const COLOR_AVAILABLE = '#E65100'; // orange line, matches boss's chart
const COLOR_DEMAND = '#5E35B2'; // purple area
const COLOR_FORECAST = '#42A5F5'; // light blue area

const fmt = (v) => Math.round(v).toLocaleString();

/**
 * Build the per-month series. Window: 3 months back, 12 months forward.
 * Demand/Forecast both 0 until live quote data is integrated — when
 * that lands, populate the demandHours / forecastHours fields here.
 *
 * @param engineers  the whole roster (availableHours filters it)
 * @param edits      the calendar, { 'Name|YYYY-MM-DD': 'Project' }
 * @param details    each scheduled day's { status, location, projectNumber }
 * @param layout     the calendar tables (which count toward utilization)
 */
export function buildForecastSeries({ mode = 'hours', engineers, edits, details, layout, today = new Date() }) {
  const months = [];
  for (let i = -3; i <= 12; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    const cap = availableHours({ engineers, edits, details, layout, year: d.getFullYear(), monthIndex: d.getMonth() });
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      capacityHours: cap.hours,
      demandHours: 0,
      forecastHours: 0,
      isCurrent: i === 0,
    });
  }
  // A month with no capacity (nobody on the roster yet) is 0%, not NaN.
  const share = (h, cap) => (cap > 0 ? (h / cap) * 100 : 0);
  return months.map((m) => {
    if (mode === 'percent') {
      return {
        ...m,
        Available: m.capacityHours > 0 ? 100 : 0,
        Demand: share(m.demandHours, m.capacityHours),
        Forecast: share(m.forecastHours, m.capacityHours),
      };
    }
    return {
      ...m,
      Available: m.capacityHours,
      Demand: m.demandHours,
      Forecast: m.forecastHours,
    };
  });
}

export function UtilizationForecastChart({ mode = 'hours', engineers, edits, details, layout }) {
  const data = buildForecastSeries({ mode, engineers, edits, details, layout });
  // Capacity now moves month to month (weekday count, PTO), so the y-axis is
  // sized off the biggest month rather than one fixed number.
  const peak = Math.max(0, ...data.map((m) => m.capacityHours));
  const yFmt = mode === 'percent' ? (v) => `${Math.round(v)}%` : (v) => `${fmt(v)}h`;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 10, right: 24, left: 8, bottom: 8 }}>
        <defs>
          <linearGradient id="forecastGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={COLOR_FORECAST} stopOpacity={0.55} />
            <stop offset="100%" stopColor={COLOR_FORECAST} stopOpacity={0.12} />
          </linearGradient>
          <linearGradient id="demandGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={COLOR_DEMAND} stopOpacity={0.7} />
            <stop offset="100%" stopColor={COLOR_DEMAND} stopOpacity={0.2} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 10, fill: '#888', fontFamily: 'Montserrat', fontWeight: 700 }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 9, fill: '#999', fontFamily: 'Montserrat' }}
          axisLine={false}
          tickLine={false}
          width={56}
          tickFormatter={yFmt}
          domain={mode === 'percent' ? [0, 120] : [0, peak > 0 ? peak * 1.2 : 'auto']}
        />
        <Tooltip
          contentStyle={{
            fontFamily: 'Montserrat',
            fontSize: 11,
            borderRadius: 8,
            border: '1px solid #E8E8E8',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
          formatter={(value, name) => [yFmt(value), name]}
        />
        <Legend iconType="circle" wrapperStyle={{ fontFamily: 'Montserrat', fontSize: 10.5, fontWeight: 700, paddingTop: 8 }} />
        {/* Stacked areas for Demand + Forecast (zero until live quote integration) */}
        <Area type="monotone" dataKey="Demand" stackId="x" stroke={COLOR_DEMAND} strokeWidth={2} fill="url(#demandGrad)" />
        <Area type="monotone" dataKey="Forecast" stackId="x" stroke={COLOR_FORECAST} strokeWidth={2} fill="url(#forecastGrad)" />
        {/* Available line — sits on top, drawn last so it's never hidden */}
        <Line type="monotone" dataKey="Available" stroke={COLOR_AVAILABLE} strokeWidth={2.5} dot={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
